/**
 * Home page component that serves as the main landing page of the application.
 * Features user authentication, privacy policy acceptance, and responsive design.
 */

import { useMsal } from "@azure/msal-react";
import Button from "../components/Button";
import Profile from "./Profile";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/Card";
import Logo from "../assets/logo.png";
import PrivacyPolicyModal from "../components/PrivacyPolicyModal";

/**
 * Home component that renders the main landing page.
 * Includes authentication state handling, privacy policy management, and responsive layout.
 *
 * @returns {JSX.Element} The rendered Home component
 */
const Home = () => {
  const { instance, accounts } = useMsal();
  const { t, i18n } = useTranslation();
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Effect hook to initialize language and check privacy policy acceptance
   */
  useEffect(() => {
    i18n.changeLanguage("fi");
    // Check if user has already accepted the privacy policy
    const hasAcceptedPolicy = localStorage.getItem("privacyPolicyAccepted");
    if (!hasAcceptedPolicy) {
      setShowPrivacyPolicy(true);
    }
  }, [i18n]);

  /**
   * Handles the Azure AD login process
   */
  const handleLogin = () => {
    instance.loginPopup().catch((e) => console.error(e));
  };

  /**
   * Handles privacy policy acceptance and stores the user's choice
   */
  const handleAcceptPolicy = () => {
    localStorage.setItem("privacyPolicyAccepted", "true");
    setShowPrivacyPolicy(false);
  };

  /**
   * Handles manual privacy policy display when clicking the footer link
   * @param {Event} e - The click event
   */
  const handlePrivacyPolicyClick = (e) => {
    e.preventDefault();
    setShowPrivacyPolicy(true);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Main Content */}
      <main className="w-full flex-grow flex items-center justify-center p-2 mt-16">
        {" "}
        {/* Added mt-16 for navbar space */}
        {accounts.length > 0 ? (
          <div className="w-full p-0">
            <Profile />
          </div>
        ) : (
          <Card className="w-full max-w-5xl overflow-hidden rounded-2xl shadow-2xl transform hover:scale-[1.01] transition-transform duration-300">
            <div className="flex flex-col md:flex-row min-h-[500px]">
              {" "}
              {/* Reduced height */}
              {/* Left side - Image */}
              <div className="relative w-full md:w-1/2 min-h-[300px] md:min-h-full">
                {" "}
                {/* Reduced height */}
                <div className="absolute inset-0">
                  <img
                    src="./water-bg.jpg"
                    alt="Background"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>
                </div>
              </div>
              {/* Right side - Login Form */}
              <div className="w-full md:w-1/2 bg-white p-8">
                {" "}
                {/* Reduced padding */}
                <CardHeader className="space-y-4">
                  {" "}
                  {/* Reduced spacing */}
                  <CardTitle className="text-3xl font-bold text-center bg-gradient-to-r from-[#cabb78] to-[#b5a666] bg-clip-text text-transparent">
                    {t("Welcome")}
                  </CardTitle>
                  <div className="w-20 h-1 mx-auto bg-gradient-to-r from-[#cabb78] to-[#b5a666] rounded-full"></div>
                </CardHeader>
                <CardContent className="mt-8">
                  {" "}
                  {/* Reduced margin */}
                  <Button
                    onClick={handleLogin}
                    className="w-full bg-gradient-to-r from-[#cabb78] to-[#b5a666] text-white py-3 px-6 rounded-lg 
                             hover:from-[#b5a666] hover:to-[#cabb78] transform hover:scale-105 
                             transition-all duration-300 shadow-lg hover:shadow-xl
                             flex items-center justify-center space-x-3 text-base" /* Adjusted padding and text size */
                  >
                    <svg
                      className="w-6 h-6" /* Reduced icon size */
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
                    </svg>
                    <span>{t("login_with_azure")}</span>
                  </Button>
                  <div className="mt-8 flex items-center justify-center space-x-4">
                    {" "}
                    {/* Reduced margin and spacing */}
                    <div className="w-2 h-2 rounded-full bg-[#cabb78] animate-pulse"></div>{" "}
                    {/* Reduced size */}
                    <div className="w-2 h-2 rounded-full bg-[#b5a666] animate-pulse delay-100"></div>
                    <div className="w-2 h-2 rounded-full bg-[#cabb78] animate-pulse delay-200"></div>
                  </div>
                </CardContent>
              </div>
            </div>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white shadow-lg mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-4">
          {" "}
          {/* Reduced padding */}
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Left side */}
            <div className="flex items-center space-x-4">
              <img
                src={Logo}
                alt="SKM Logo"
                className="h-8 w-auto" /* Reduced size */
              />
              <div className="text-gray-600">
                <p className="font-semibold">Portal</p>
                <p className="text-sm">
                  © {new Date().getFullYear()} {t("all_rights_reserved")}
                </p>
              </div>
            </div>

            {/* Center */}
            <div className="flex space-x-6">
              <a
                href="#"
                className="text-gray-600 hover:text-[#cabb78] transition-colors duration-300"
                onClick={handlePrivacyPolicyClick}
              >
                {t("Privacy_policy")}
              </a>
              <a
                href="/contact"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/contact", {
                    state: { backgroundLocation: location },
                  });
                }}
                className="text-gray-600 hover:text-[#cabb78] transition-colors duration-300"
              >
                {t("contct")}
              </a>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              <a
                href="https://www.linkedin.com/in/kaarlohirvi/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-[#cabb78] transition-colors duration-300"
              >
                <svg
                  className="h-5 w-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      <PrivacyPolicyModal
        isOpen={showPrivacyPolicy}
        onAccept={handleAcceptPolicy}
      />
    </div>
  );
};

export default Home;
